/* The estate in one strip: the number, which way it moved, and how the population splits.
 *
 * **Compact by design, and not a row of cards.** Seven figures a reader scans in a second, in one
 * bordered band, because the job of this section is to be finished with quickly. A grid of tiles
 * would make each figure look equally important and push the ledger — which is where the work is —
 * below the fold.
 *
 * Four of the cells are buttons: the population counts *are* the saved views, so pressing "Critical"
 * filters the table rather than sending a reader to find the same filter again in the toolbar. The
 * two that are not buttons have no filter to be — an estate mean is not a subset.
 *
 * Every figure is served: `value`, `delta`, `previous_value`, `scored` and `columns_measured` come
 * from `GET /v1/scoring/estate`, one aggregate for the whole tenant. The population split is
 * counted from the rows already on screen, so the strip and the table can never disagree.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SkeletonRows } from '@/shared/ui/Skeleton';

import type { EstateSummary } from '../api/scoring';
import { formatScore } from '../model/presentation';
import type { ScoreStatus } from '../model/types';

export interface QualitySummaryProps {
  readonly estate: EstateSummary | undefined;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly counts: Readonly<Record<ScoreStatus, number>>;
  readonly total: number;
  /** Scored runs inside the window, summed from the movement reads. `null` when those are off — a
   * count nobody has read, which is not a zero. */
  readonly runsInWindow: number | null;
  readonly onSelectView: (view: string) => void;
  readonly activeView: string;
}

function Cell({
  label,
  value,
  sub,
  tone,
  view,
  activeView,
  onSelect,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly tone?: 'ok' | 'warn' | 'bad' | undefined;
  readonly view?: string | undefined;
  readonly activeView?: string | undefined;
  readonly onSelect?: ((view: string) => void) | undefined;
}): ReactElement {
  const body = (
    <>
      <span className="lq-estate__label">{label}</span>
      <span
        className={
          tone === undefined
            ? 'lq-estate__value'
            : `lq-estate__value lq-estate__value--${tone}`
        }
      >
        {value}
      </span>
      <span className="lq-estate__sub">{sub}</span>
    </>
  );

  if (view === undefined || onSelect === undefined) {
    return <div className="lq-estate__cell">{body}</div>;
  }
  return (
    <button
      type="button"
      className="lq-estate__cell lq-estate__cell--pick"
      aria-pressed={activeView === view}
      onClick={() => {
        onSelect(view);
      }}
    >
      {body}
    </button>
  );
}

export function QualitySummary({
  estate,
  loading,
  error,
  counts,
  total,
  runsInWindow,
  onSelectView,
  activeView,
}: QualitySummaryProps): ReactElement {
  const { t } = useTranslation();

  if (loading) return <SkeletonRows rows={1} columns={7} />;

  /* A failed estate read does not blank the strip: the population split is counted from rows this
     screen already holds, and it is still true. Only the mean is missing, and it says so. */
  const value = estate?.value ?? null;
  const delta = estate?.delta ?? null;

  const movement =
    error !== null
      ? { text: t('scoring.estate.unreadable'), className: 'lq-muted' }
      : delta === null
        ? { text: t('scoring.estate.noBaseline'), className: 'lq-muted' }
        : delta > 0.1
          ? { text: `▲ ${formatScore(delta)}`, className: 'lq-delta lq-delta--up' }
          : delta < -0.1
            ? {
                text: `▼ ${formatScore(Math.abs(delta))}`,
                className: 'lq-delta lq-delta--down',
              }
            : { text: t('scoring.delta.flat'), className: 'lq-muted' };

  return (
    <section className="lq-estate" aria-label={t('scoring.estate.region')}>
      <div className="lq-estate__cell lq-estate__cell--hero">
        <span className="lq-estate__label">{t('scoring.estate.overall')}</span>
        <span className="lq-estate__headline">
          <span className="lq-estate__figure">
            {value === null ? t('scoring.estate.none') : formatScore(value)}
          </span>
          <span className={movement.className}>{movement.text}</span>
        </span>
        <span className="lq-estate__sub">
          {estate === undefined
            ? t('scoring.estate.subUnavailable')
            : t('scoring.estate.sub', {
                days: estate.window_days,
                columns: estate.columns_measured,
              })}
        </span>
      </div>

      <Cell
        label={t('scoring.estate.datasets')}
        value={total.toLocaleString()}
        sub={
          estate === undefined
            ? t('scoring.estate.scoredUnknown')
            : t('scoring.estate.scored', { scored: estate.scored, total })
        }
        view="all"
        activeView={activeView}
        onSelect={onSelectView}
      />
      <Cell
        label={t('scoring.estate.healthy')}
        value={counts.healthy.toLocaleString()}
        sub={t('scoring.estate.healthySub')}
        tone="ok"
      />
      <Cell
        label={t('scoring.estate.attention')}
        value={counts.warning.toLocaleString()}
        sub={t('scoring.estate.attentionSub')}
        tone="warn"
        view="attention"
        activeView={activeView}
        onSelect={onSelectView}
      />
      <Cell
        label={t('scoring.estate.critical')}
        value={counts.critical.toLocaleString()}
        sub={t('scoring.estate.criticalSub')}
        tone="bad"
        view="critical"
        activeView={activeView}
        onSelect={onSelectView}
      />
      <Cell
        label={t('scoring.estate.unscored')}
        value={counts.unscored.toLocaleString()}
        sub={t('scoring.estate.unscoredSub')}
        view="unscored"
        activeView={activeView}
        onSelect={onSelectView}
      />
      <Cell
        label={t('scoring.estate.runs')}
        value={
          runsInWindow === null
            ? t('scoring.estate.runsUnread')
            : runsInWindow.toLocaleString()
        }
        sub={
          runsInWindow === null
            ? t('scoring.estate.runsUnreadSub')
            : t('scoring.estate.runsSub', { days: estate?.window_days ?? 0 })
        }
      />
    </section>
  );
}
